import { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronUp } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const TABS = [
  { key: 'recs', label: 'Recommendations' },
  { key: 'preds', label: 'Predictions' },
  { key: 'shadows', label: 'Shadow Trades' },
  { key: 'reflections', label: 'Reflections' },
  { key: 'real', label: 'Real Trades' },
];

function formatTs(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

function PnlBadge({ pct }) {
  if (pct == null) return <span className="text-korinth-muted">—</span>;
  return <span className={`font-bold ${pct > 0 ? 'text-korinth-accent' : pct < 0 ? 'text-korinth-danger' : 'text-korinth-muted'}`}>{pct >= 0 ? '+' : ''}{pct.toFixed(2)}%</span>;
}

function StatusBadge({ status }) {
  const colors = { active: 'bg-korinth-warning/10 text-korinth-warning', replaced: 'bg-korinth-muted/10 text-korinth-muted', expired: 'bg-gray-500/10 text-gray-400', correct: 'bg-korinth-accent/10 text-korinth-accent', wrong: 'bg-korinth-danger/10 text-korinth-danger', open: 'bg-blue-500/10 text-blue-400', closed: 'bg-korinth-muted/10 text-korinth-muted', superseded: 'bg-purple-500/10 text-purple-400', merged_into: 'bg-korinth-muted/10 text-korinth-muted', resolved: 'bg-korinth-accent/10 text-korinth-accent' };
  return <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${colors[status] || 'bg-korinth-border text-korinth-muted'}`}>{status}</span>;
}

function PredictionScorecard({ predictions }) {
  const resolved = predictions.filter(p => p.outcome && p.outcome !== 'merged');
  const correct = resolved.filter(p => p.outcome === 'correct').length;
  const wrong = resolved.filter(p => p.outcome === 'wrong').length;
  const expired = resolved.filter(p => p.outcome === 'expired').length;
  const open = predictions.filter(p => p.status === 'open').length;
  const rawHitRate = resolved.length > 0 ? (correct / resolved.length * 100).toFixed(0) : '—';

  // Unique thesis hit rate: dedup by asset+direction+timeframe
  const uniqueResolved = new Map();
  for (const p of resolved) {
    const key = `${p.asset}:${p.direction}:${p.timeframe}`;
    if (!uniqueResolved.has(key) || p.outcome === 'correct') uniqueResolved.set(key, p);
  }
  const uniqueCorrect = [...uniqueResolved.values()].filter(p => p.outcome === 'correct').length;
  const uniqueHitRate = uniqueResolved.size > 0 ? (uniqueCorrect / uniqueResolved.size * 100).toFixed(0) : '—';

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      <div className="bg-korinth-bg rounded-lg px-3 py-2 text-center border border-korinth-border/30">
        <div className="text-lg font-mono font-bold text-white">{open}</div>
        <div className="text-[9px] text-blue-400">Open</div>
      </div>
      <div className="bg-korinth-bg rounded-lg px-3 py-2 text-center border border-korinth-border/30">
        <div className="text-lg font-mono font-bold text-korinth-accent">{correct}</div>
        <div className="text-[9px] text-korinth-accent">Correct</div>
      </div>
      <div className="bg-korinth-bg rounded-lg px-3 py-2 text-center border border-korinth-border/30">
        <div className="text-lg font-mono font-bold text-korinth-danger">{wrong}</div>
        <div className="text-[9px] text-korinth-danger">Incorrect</div>
      </div>
      <div className="bg-korinth-bg rounded-lg px-3 py-2 text-center border border-korinth-border/30">
        <div className="text-lg font-mono font-bold text-gray-400">{expired}</div>
        <div className="text-[9px] text-gray-400">Expired</div>
      </div>
      <div className="bg-korinth-bg rounded-lg px-3 py-2 text-center border border-korinth-border/30 lg:col-span-2">
        <div className="text-lg font-mono font-bold text-korinth-text">{rawHitRate}%</div>
        <div className="text-[9px] text-korinth-muted">Raw Hit Rate ({correct}/{resolved.length})</div>
      </div>
      <div className="bg-korinth-bg rounded-lg px-3 py-2 text-center border border-korinth-accent/30 lg:col-span-2">
        <div className="text-lg font-mono font-bold text-korinth-accent">{uniqueHitRate}%</div>
        <div className="text-[9px] text-korinth-accent">Unique Thesis Hit Rate ({uniqueCorrect}/{uniqueResolved.size})</div>
      </div>
    </div>
  );
}

export default function HistoryPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('history'); }, []);

  const [tab, setTab] = useState('recs');
  const [assetFilter, setAssetFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchData(); }, [tab]);

  async function fetchData() {
    setLoading(true);
    try {
      let url;
      if (tab === 'recs') url = '/api/kairos/recommendations?limit=100';
      else if (tab === 'preds') url = '/api/kairos/predictions?limit=200&include_resolved=1';
      else if (tab === 'shadows') url = '/api/shadow/positions?status=closed&limit=100';
      else if (tab === 'reflections') url = '/api/kairos/lessons?days=90';
      else { setData([]); setLoading(false); return; }

      const res = await fetch(url);
      if (res.ok) {
        const d = await res.json();
        setData(d.recommendations || d.predictions || d.lessons || d || []);
      }
    } catch {} finally { setLoading(false); }
  }

  const ASSET_CLASSES = {
    currencies: ['EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'CNY', 'USD Cash', 'EUR/USD', 'GBP/USD', 'USD/CNY'],
    crypto: ['Bitcoin', 'BTC', 'Ethereum', 'ETH', 'XRP', 'Solana', 'SOL', 'HBAR'],
    equities: ['US Equities', 'Nasdaq', 'SPX', 'NDX'],
    metals: ['Gold', 'Silver', 'Copper', 'Platinum'],
  };

  const outcomeFiltered = (tab === 'preds' && outcomeFilter !== 'all')
    ? data.filter(item => outcomeFilter === 'incorrect' ? item.outcome === 'wrong' : outcomeFilter === 'resolved' ? item.resolved_at != null : item.status === outcomeFilter)
    : data;
  const filteredData = assetFilter === 'all' ? outcomeFiltered : outcomeFiltered.filter(item => {
    const asset = item.asset || item.instrument || '';
    return (ASSET_CLASSES[assetFilter] || []).some(a => asset.toLowerCase().includes(a.toLowerCase()));
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Clock className="w-6 h-6 text-korinth-warning" />
        <h1 className="text-2xl font-bold text-white">History</h1>
        <span className="text-korinth-muted text-sm">Everything KAIROS has decided</span>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-2 rounded-lg text-xs font-mono font-bold whitespace-nowrap ${
              tab === t.key ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'text-korinth-muted border border-transparent hover:bg-korinth-border/50'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Asset class filter */}
      <div className="flex gap-1 flex-wrap">
        {['all', 'currencies', 'crypto', 'equities', 'metals'].map(f => (
          <button key={f} onClick={() => setAssetFilter(f)}
            className={`px-2 py-1 rounded text-[10px] font-mono ${assetFilter === f ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'text-korinth-muted border border-transparent hover:bg-korinth-border/50'}`}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'preds' && !loading && <PredictionScorecard predictions={data} />}

      {tab === 'preds' && !loading && (
        <div className="flex gap-1 flex-wrap">
          {['all', 'open', 'resolved', 'incorrect'].map(f => (
            <button key={f} onClick={() => setOutcomeFilter(f)}
              className={`px-2 py-1 rounded text-[10px] font-mono ${outcomeFilter === f ? 'bg-korinth-accent/10 text-korinth-accent border border-korinth-accent' : 'text-korinth-muted border border-transparent hover:bg-korinth-border/50'}`}>
              {f === 'all' ? 'All' : f === 'incorrect' ? 'Incorrect only' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      )}

      <ModuleContainer title={`${TABS.find(t => t.key === tab)?.label} (${filteredData.length})`}>
        {tab === 'real' ? (
          <p className="text-xs text-korinth-muted font-mono text-center py-8">Real trade history — coming in Sprint 11+ when live execution is enabled.</p>
        ) : loading ? (
          <p className="text-xs text-korinth-muted font-mono text-center py-8">Loading...</p>
        ) : filteredData.length === 0 ? (
          <p className="text-xs text-korinth-muted font-mono text-center py-8">No data yet</p>
        ) : (
          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {filteredData.map((item, i) => {
              const id = item.id || i;
              const isExp = expanded === id;

              if (tab === 'recs') return (
                <div key={id} className="bg-korinth-bg rounded border border-korinth-border/30">
                  <button onClick={() => setExpanded(isExp ? null : id)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-mono">
                    <span className={`font-bold w-16 ${item.direction === 'buy' ? 'text-korinth-accent' : item.direction === 'sell' ? 'text-korinth-danger' : 'text-korinth-warning'}`}>{item.direction?.toUpperCase()}</span>
                    <span className="text-white font-bold w-20">{item.asset}</span>
                    <span className="text-korinth-text">{item.conviction_overall?.toFixed(1)}/10</span>
                    <StatusBadge status={item.status} />
                    <span className="text-korinth-muted/60 ml-auto">{formatTs(item.created_at)}</span>
                    {isExp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {isExp && (
                    <div className="px-3 pb-2 border-t border-korinth-border/20 pt-2 text-[10px] font-mono text-korinth-muted space-y-1">
                      <p className="text-korinth-text">{typeof item.reasoning === 'string' ? item.reasoning : JSON.stringify(item.reasoning)?.slice(0, 300)}</p>
                      {item.exit_reason && <p>Exit: {item.exit_reason} at {formatTs(item.exited_at)}</p>}
                      {item.exit_triggers && <p>Triggers: {JSON.stringify(item.exit_triggers)?.slice(0, 200)}</p>}
                    </div>
                  )}
                </div>
              );

              if (tab === 'preds') {
                const outcomeColor = item.outcome === 'correct' ? 'border-l-korinth-accent' : item.outcome === 'wrong' ? 'border-l-korinth-danger' : item.outcome === 'expired' ? 'border-l-gray-500' : 'border-l-blue-500';
                const confHistory = item.confidence_history ? (typeof item.confidence_history === 'string' ? JSON.parse(item.confidence_history) : item.confidence_history) : [];
                return (
                  <div key={id} className={`bg-korinth-bg rounded border border-korinth-border/30 border-l-3 ${outcomeColor}`}>
                    <button onClick={() => setExpanded(isExp ? null : id)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-mono">
                      <span className={item.direction === 'bullish' ? 'text-korinth-accent' : 'text-korinth-danger'}>{item.direction === 'bullish' ? '▲' : '▼'}</span>
                      <span className="text-white font-bold w-16">{item.asset}</span>
                      <span className="text-korinth-muted px-1 py-0.5 rounded bg-korinth-border text-[8px]">{item.timeframe}</span>
                      <span className="text-korinth-text">{item.confidence}%</span>
                      <StatusBadge status={item.outcome || item.status || 'open'} />
                      {(item.update_count || 0) > 0 && <span className="text-[8px] px-1 py-0.5 rounded bg-purple-500/10 text-purple-400">Updated {item.update_count}x</span>}
                      <span className="text-korinth-muted/60 ml-auto">{formatTs(item.first_created_at || item.created_at)}</span>
                    </button>
                    {isExp && (
                      <div className="px-3 pb-2 border-t border-korinth-border/20 pt-2 text-[10px] font-mono text-korinth-muted space-y-1">
                        {item.reasoning_chain && <p className="text-korinth-text">{typeof item.reasoning_chain === 'string' ? (() => { try { return JSON.parse(item.reasoning_chain).join(' • '); } catch { return item.reasoning_chain; } })() : JSON.stringify(item.reasoning_chain)?.slice(0, 300)}</p>}
                        {item.target_price && <p>Target: {item.target_price} | Stop: {item.invalidation_price || '—'}</p>}
                        {item.actual_price && <p>Resolved at: {item.actual_price}</p>}
                        {confHistory.length > 0 && (
                          <div>
                            <p className="text-korinth-accent text-[9px] mt-1">Confidence trajectory:</p>
                            {confHistory.map((h, hi) => (
                              <span key={hi} className="text-[9px] mr-2">{h.confidence}% <span className="text-korinth-muted/50">({h.observed_at?.slice(5, 16)})</span></span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (tab === 'shadows') return (
                <div key={id} className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded text-xs font-mono">
                  <span className={`font-bold ${item.direction === 'long' ? 'text-korinth-accent' : item.direction === 'short' ? 'text-korinth-danger' : 'text-blue-400'}`}>{item.direction?.toUpperCase()}</span>
                  <span className="text-white w-16">{item.asset}</span>
                  <span className="text-korinth-muted">{item.entry_price?.toFixed(2)} → {item.exit_price?.toFixed(2)}</span>
                  <PnlBadge pct={item.realized_pnl_pct} />
                  <span className="text-korinth-muted/60 ml-auto">{item.exit_reason}</span>
                </div>
              );

              if (tab === 'reflections') return (
                <div key={id} className="px-3 py-2 bg-korinth-bg rounded border-l-2 border-purple-400/30">
                  <p className="text-xs text-korinth-text">{item.lesson_text}</p>
                  <p className="text-[9px] text-korinth-muted mt-1">{item.source} — {item.created_at?.slice(0, 10)}</p>
                </div>
              );

              return null;
            })}
          </div>
        )}
      </ModuleContainer>
    </div>
  );
}
